import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

interface Position {
  left: number; // absolute left position of tooltip container
  y: number;
  placement: 'top' | 'bottom';
  arrowLeft: number; // Absolute left position for arrow inside tooltip
}

const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  className = '',
  delay = 150 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  // Ensure component is mounted before showing portal
  useEffect(() => {
    setIsMounted(true);
    
    // Detect touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    // Create or get tooltip container at document body level
    let tooltipRoot = document.getElementById('tooltip-root');
    if (!tooltipRoot) {
      tooltipRoot = document.createElement('div');
      tooltipRoot.id = 'tooltip-root';
      tooltipRoot.style.position = 'fixed';
      tooltipRoot.style.top = '0';
      tooltipRoot.style.left = '0';
      tooltipRoot.style.width = '0';
      tooltipRoot.style.height = '0';
      tooltipRoot.style.pointerEvents = 'none';
      tooltipRoot.style.zIndex = '2147483647'; // Maximum z-index value
      tooltipRoot.style.isolation = 'isolate'; // Create new stacking context
      document.body.appendChild(tooltipRoot);
    }
    
    return () => setIsMounted(false);
  }, []);

  // Calculate position when tooltip becomes visible
  useEffect(() => {
    if (!isVisible || !triggerRef.current) return;

    const calculatePosition = () => {
      if (!triggerRef.current) return;
      
      const rect = triggerRef.current.getBoundingClientRect();
      
      // Get actual tooltip dimensions if it exists
      const tooltipEl = document.getElementById('tooltip-content');
      const isMobile = window.innerWidth < 768;
      const tooltipWidth = tooltipEl ? tooltipEl.offsetWidth : (isMobile ? Math.min(280, window.innerWidth - 20) : 320); // Ensure fits on very small screens
      const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 120; // Increased estimated height
      const spacing = isMobile ? 8 : 12; // Reduced spacing on mobile
      const edgeMargin = isMobile ? 10 : 20; // Smaller margins on mobile
      
      // Always use the center of the trigger element (text center)
      const triggerCenterX = rect.left + rect.width / 2;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate tooltip position centered on the text
      const halfWidth = tooltipWidth / 2;
      
      // Start with tooltip centered on the text
      let tooltipX = triggerCenterX;
      
      // Check if tooltip goes off left edge
      if (tooltipX - halfWidth < edgeMargin) {
        // Shift tooltip right to fit
        tooltipX = edgeMargin + halfWidth;
      }
      // Check if tooltip goes off right edge
      else if (tooltipX + halfWidth > viewportWidth - edgeMargin) {
        // Shift tooltip left to fit
        tooltipX = viewportWidth - edgeMargin - halfWidth;
      }
      
      // Calculate arrow position - always points to trigger center
      // Arrow position is relative to the tooltip's left edge
      const tooltipLeft = tooltipX - halfWidth;
      let arrowLeft = triggerCenterX - tooltipLeft;
      
      // Ensure arrow stays within tooltip bounds (at least 15px from edges)
      const minArrowPos = 15;
      const maxArrowPos = tooltipWidth - 15;
      arrowLeft = Math.max(minArrowPos, Math.min(maxArrowPos, arrowLeft));
      
      // Force top placement on mobile, smart placement on desktop
      let placement: 'top' | 'bottom' = isMobile ? 'top' : 'bottom';
      let y = isMobile ? rect.top - spacing : rect.bottom + spacing;
      
      if (!isMobile) {
        // Desktop: Smart placement based on available space
        const spaceBelow = viewportHeight - rect.bottom - spacing;
        const spaceAbove = rect.top - spacing;
        
        if (spaceBelow < tooltipHeight + edgeMargin) {
          if (spaceAbove > tooltipHeight + edgeMargin) {
            // Show above if there's enough space
            placement = 'top';
            y = rect.top - spacing;
          } else {
            // If neither has enough space, prefer the side with more room
            if (spaceAbove > spaceBelow) {
              placement = 'top';
              y = Math.max(edgeMargin, rect.top - spacing);
            } else {
              // Keep below but ensure it doesn't go off screen
              y = Math.min(rect.bottom + spacing, viewportHeight - tooltipHeight - edgeMargin);
            }
          }
        }
      } else {
        // Mobile: Smart placement to avoid clipping
        const spaceAbove = rect.top - spacing;
        const spaceBelow = viewportHeight - rect.bottom - spacing;
        
        // Prefer showing above, but switch to below if not enough space
        if (spaceAbove >= tooltipHeight + edgeMargin) {
          placement = 'top';
          y = rect.top - spacing;
        } else if (spaceBelow >= tooltipHeight + edgeMargin) {
          placement = 'bottom';
          y = rect.bottom + spacing;
        } else {
          // Neither position has enough space, choose the side with more room
          if (spaceAbove > spaceBelow) {
            placement = 'top';
            y = Math.max(tooltipHeight + edgeMargin, rect.top - spacing);
          } else {
            placement = 'bottom';
            y = Math.min(rect.bottom + spacing, viewportHeight - tooltipHeight - edgeMargin);
          }
        }
      }
      
      // Final safety check: ensure tooltip is within viewport bounds
      const finalTooltipLeft = Math.max(edgeMargin, Math.min(tooltipLeft, viewportWidth - tooltipWidth - edgeMargin));
      const finalY = placement === 'top' 
        ? Math.max(tooltipHeight + edgeMargin, y)
        : Math.min(y, viewportHeight - tooltipHeight - edgeMargin);
      
      // Recalculate arrow position if tooltip was repositioned
      let finalArrowLeft = arrowLeft;
      if (finalTooltipLeft !== tooltipLeft) {
        finalArrowLeft = triggerCenterX - finalTooltipLeft;
        finalArrowLeft = Math.max(15, Math.min(tooltipWidth - 15, finalArrowLeft));
      }
      
      setPosition({ left: finalTooltipLeft, y: finalY, placement, arrowLeft: finalArrowLeft });
    };

    calculatePosition();
    
    // Recalculate on scroll/resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);
    
    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible]);

  // After initial render of the tooltip, measure real size and correct the position
  useEffect(() => {
    if (!isVisible || !triggerRef.current) return;

    const tooltipEl = document.getElementById('tooltip-content');
    if (!tooltipEl) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    const spacing = isMobile ? 8 : 12;
    const edgeMargin = isMobile ? 10 : 20;

    const triggerCenterX = rect.left + rect.width / 2;
    const halfWidth = tooltipRect.width / 2;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Recompute horizontal position with real width
    let tooltipX = triggerCenterX;
    if (tooltipX - halfWidth < edgeMargin) {
      tooltipX = edgeMargin + halfWidth;
    } else if (tooltipX + halfWidth > viewportWidth - edgeMargin) {
      tooltipX = viewportWidth - edgeMargin - halfWidth;
    }

    // Compute arrow left relative to tooltip
    const tooltipLeft = tooltipX - halfWidth;
    let arrowLeft = triggerCenterX - tooltipLeft;
    const minArrowPos = 15;
    const maxArrowPos = tooltipRect.width - 15;
    arrowLeft = Math.max(minArrowPos, Math.min(maxArrowPos, arrowLeft));

    // Recompute vertical position/placement based on real height
    let placement: 'top' | 'bottom' = isMobile ? 'top' : 'bottom';
    let y = isMobile ? rect.top - spacing : rect.bottom + spacing;

    if (!isMobile) {
      const spaceBelow = viewportHeight - rect.bottom - spacing;
      const spaceAbove = rect.top - spacing;
      if (spaceBelow < tooltipRect.height + edgeMargin) {
        if (spaceAbove > tooltipRect.height + edgeMargin) {
          placement = 'top';
          y = rect.top - spacing;
        } else {
          if (spaceAbove > spaceBelow) {
            placement = 'top';
            y = Math.max(edgeMargin, rect.top - spacing);
          } else {
            y = Math.min(rect.bottom + spacing, viewportHeight - tooltipRect.height - edgeMargin);
          }
        }
      }
    } else {
      // Mobile: Smart placement with real height measurements
      const spaceAbove = rect.top - spacing;
      const spaceBelow = viewportHeight - rect.bottom - spacing;
      
      // Prefer showing above, but switch to below if not enough space
      if (spaceAbove >= tooltipRect.height + edgeMargin) {
        placement = 'top';
        y = rect.top - spacing;
      } else if (spaceBelow >= tooltipRect.height + edgeMargin) {
        placement = 'bottom';
        y = rect.bottom + spacing;
      } else {
        // Neither position has enough space, choose the side with more room
        if (spaceAbove > spaceBelow) {
          placement = 'top';
          y = Math.max(tooltipRect.height + edgeMargin, rect.top - spacing);
        } else {
          placement = 'bottom';
          y = Math.min(rect.bottom + spacing, viewportHeight - tooltipRect.height - edgeMargin);
        }
      }
    }

    // Final safety check: ensure tooltip is within viewport bounds
    const finalTooltipLeft = Math.max(edgeMargin, Math.min(tooltipLeft, viewportWidth - tooltipRect.width - edgeMargin));
    const finalY = placement === 'top' 
      ? Math.max(tooltipRect.height + edgeMargin, y)
      : Math.min(y, viewportHeight - tooltipRect.height - edgeMargin);
    
    // Recalculate arrow position if tooltip was repositioned
    let finalArrowLeft = arrowLeft;
    if (finalTooltipLeft !== tooltipLeft) {
      finalArrowLeft = triggerCenterX - finalTooltipLeft;
      finalArrowLeft = Math.max(15, Math.min(tooltipRect.width - 15, finalArrowLeft));
    }
    
    setPosition({ left: finalTooltipLeft, y: finalY, placement, arrowLeft: finalArrowLeft });
  }, [isVisible, content]);

  const handleMouseEnter = () => {
    // Don't show on hover for touch devices
    if (isTouchDevice) return;
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    // Don't hide on mouse leave for touch devices if tooltip was opened via click
    if (isTouchDevice && isVisible) return;
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any link navigation
    setIsVisible(!isVisible);
  };

  // Close on Escape or outside click
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target) return;
      
      // Check if click is outside tooltip
      const tooltipEl = document.getElementById('tooltip-content');
      const triggerEl = document.getElementById('tooltip-trigger');
      
      if (tooltipEl && !tooltipEl.contains(target) && 
          triggerEl && !triggerEl.contains(target)) {
        setIsVisible(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Delay the outside click listener to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isVisible]);


  // Split content by || to create multiple lines
  const lines = content.split('||');

  return (
    <>
      <span
        ref={triggerRef}
        id="tooltip-trigger"
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        role="button"
        aria-describedby={isVisible ? 'tooltip-content' : undefined}
        tabIndex={0}
      >
        {children}
      </span>
      
      {isMounted && position && document.getElementById('tooltip-root') && createPortal(
        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: position.placement === 'top' ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: position.placement === 'top' ? 4 : -4 }}
              transition={{ duration: 0.15 }}
              style={{
                position: 'fixed',
                left: `${position.left}px`,
                top: position.placement === 'bottom' 
                  ? `${position.y}px`
                  : undefined,
                bottom: position.placement === 'top'
                  ? `${window.innerHeight - position.y}px`
                  : undefined,
                zIndex: 2147483646, // Just below the root container
                pointerEvents: 'auto', // Enable interaction for accessibility
                isolation: 'isolate' // Create new stacking context
              }}
              id="tooltip-content"
              role="tooltip"
            >
              {/* Arrow - positioned to point at trigger */}
              <div 
                className={`absolute ${
                  position.placement === 'top' 
                    ? 'bottom-[-6px]' 
                    : 'top-[-6px]'
                }`}
                style={{
                  left: `${position.arrowLeft}px`,
                  transform: 'translateX(-50%)',
                }}
              >
                <div className="relative">
                  {/* Arrow shadow/glow */}
                  <div 
                    className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 
                              border-l-[8px] border-l-transparent
                              border-r-[8px] border-r-transparent
                              ${
                                position.placement === 'top'
                                  ? 'border-t-[10px] border-t-primary/30'
                                  : 'border-b-[10px] border-b-primary/30'
                              } blur-sm`}
                  />
                  {/* Main arrow */}
                  <div 
                    className={`relative w-0 h-0 
                              border-l-[6px] border-l-transparent
                              border-r-[6px] border-r-transparent
                              ${
                                position.placement === 'top'
                                  ? 'border-t-[8px] border-t-foreground'
                                  : 'border-b-[8px] border-b-foreground'
                              }`}
                  />
                </div>
              </div>
              
              {/* Tooltip content */}
              <div className="relative">
                {/* Glow effect - reduced on mobile */}
                <motion.div 
                  className="absolute inset-0 bg-primary/20 blur-xl rounded-lg hidden md:block"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Main tooltip box */}
                <div className="relative bg-foreground text-background px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg shadow-2xl border border-primary/20 max-w-[calc(100vw-20px)] sm:max-w-[min(calc(100vw-40px),320px)] md:max-w-[320px] pointer-events-none overflow-hidden">
                  <div className="space-y-1.5">
                    {lines.map((line, index) => (
                      <p key={index} className="text-[11px] sm:text-xs md:text-sm leading-relaxed whitespace-normal break-words">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.getElementById('tooltip-root')!
      )}
    </>
  );
};

export default Tooltip;
